import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { useUIStore, type DateFormat } from '@/core/stores/ui.store';
import { useUpdatePreferences } from '../../hooks/useUpdatePreferences';
import { FloatingLabelAsyncCombobox } from '@/shared/components/custom/async-combobox/FloatingLabelAsyncCombobox';
import type { AsyncComboboxOption } from '@/shared/components/custom/async-combobox/AsyncCombobox.types';

const DATE_FORMAT_OPTIONS: AsyncComboboxOption[] = [
  { id: 'dd/MM/yyyy', label: '31/12/2024', description: 'Día/Mes/Año' },
  { id: 'MM/dd/yyyy', label: '12/31/2024', description: 'Mes/Día/Año' },
  { id: 'yyyy-MM-dd', label: '2024-12-31', description: 'Año-Mes-Día' },
  { id: 'dd-MM-yyyy', label: '31-12-2024', description: 'Día-Mes-Año' },
  { id: 'MM-dd-yyyy', label: '12-31-2024', description: 'Mes-Día-Año' },
  { id: 'dd.MM.yyyy', label: '31.12.2024', description: 'Día.Mes.Año' },
  { id: 'd MMM yyyy', label: '31 dic 2024', description: 'Día Mes Año' },
];

interface DateFormatSectionProps {
  showHeader?: boolean;
}

export function DateFormatSection({ showHeader = true }: DateFormatSectionProps) {
  const { t } = useTranslation('settings');
  const dateFormat = useUIStore((s) => s.dateFormat);
  const setDateFormat = useUIStore((s) => s.setDateFormat);
  const updatePreferences = useUpdatePreferences();

  const handleChange = (value: string | null) => {
    if (value) {
      setDateFormat(value as DateFormat);
      updatePreferences.mutate({ dateFormat: value as DateFormat });
    }
  };

  return (
    <div className="space-y-3" data-testid="settings-dateformat-section">
      {showHeader && (
        <div>
          <h2 className="text-lg font-semibold text-foreground">{t('dateFormat.title')}</h2>
          <p className="text-sm text-muted-foreground mt-1">{t('dateFormat.description')}</p>
        </div>
      )}

      <div className="space-y-3">
        <FloatingLabelAsyncCombobox
          id="settings-dateformat"
          label={t('dateFormat.title')}
          queryKey={['settings', 'dateFormats']}
          staticOptions={DATE_FORMAT_OPTIONS}
          value={dateFormat}
          onChange={handleChange}
          placeholder={t('dateFormat.placeholder')}
        />
        {updatePreferences.isPending && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="w-3 h-3 animate-spin" />
            {t('preferences.saving')}
          </div>
        )}
      </div>
    </div>
  );
}
